import React, { useEffect, useRef, useState, useMemo } from 'react';
import { motion } from 'motion/react';

export interface PriceOption {
  label: string;
  price: number;
}

interface PricePickerProps {
  productName: string;
  basePrice: number;
  priceList: PriceOption[];
  onSelect: (price: number, label: string) => void;
  onClose: () => void;
}

export function PricePicker({ productName, basePrice, priceList, onSelect, onClose }: PricePickerProps) {
  // Build options: base price + all alternate prices
  const options: PriceOption[] = useMemo(() => [
    { label: 'Default Price', price: Number(basePrice) },
    ...(priceList || []).filter(p => Number(p.price) > 0).map(p => ({ label: p.label, price: Number(p.price) })),
  ], [basePrice, priceList]);

  const [selectedIdx, setSelectedIdx] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  // Keyboard navigation with capture to prioritize modal over global POS shortcuts
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIdx(prev => Math.max(0, prev - 1));
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIdx(prev => Math.min(options.length - 1, prev + 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        const opt = options[selectedIdx];
        if (opt) {
          onSelect(opt.price, opt.label);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKey, true);
    return () => window.removeEventListener('keydown', handleKey, true);
  }, [selectedIdx, options, onSelect, onClose]);

  // Scroll selected option into view
  useEffect(() => {
    const el = listRef.current?.children[selectedIdx] as HTMLElement;
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedIdx]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[10003] bg-black/65 backdrop-blur-md flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-100"
      >
        {/* Header */}
        <div className="px-5 py-4 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-700 text-white">
          <div className="flex items-center justify-between">
            <h3 className="font-black text-base truncate flex items-center gap-2">
              <span>🏷️</span> {productName}
            </h3>
            <span className="text-[10px] bg-white/20 font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">Price Selection</span>
          </div>
          <p className="text-white/80 text-xs mt-1 font-medium">Click mouse or use ↑↓ arrow keys • Enter to select</p>
        </div>

        {/* Options */}
        <div ref={listRef} className="max-h-72 overflow-y-auto py-2 px-2 space-y-1">
          {options.map((opt, i) => {
            const isDefault = i === 0;
            const isDiscount = opt.price < basePrice;
            const isSelected = i === selectedIdx;

            return (
              <button
                key={i}
                type="button"
                onClick={() => onSelect(opt.price, opt.label)}
                onMouseEnter={() => setSelectedIdx(i)}
                className={`w-full flex items-center justify-between p-3.5 rounded-xl transition-all text-left cursor-pointer ${
                  isSelected
                    ? 'bg-emerald-50/90 border-2 border-emerald-500 shadow-sm'
                    : 'border-2 border-transparent hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                    isSelected ? 'border-emerald-600 bg-emerald-600' : 'border-slate-300'
                  }`}>
                    {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </div>
                  <div>
                    <p className={`text-sm font-black ${isSelected ? 'text-emerald-950' : 'text-slate-800'}`}>
                      {opt.label}
                    </p>
                    {isDefault && (
                      <p className="text-[10px] text-slate-400 font-medium">Standard Retail Price</p>
                    )}
                    {isDiscount && (
                      <p className="text-[10px] text-emerald-600 font-bold">
                        Save Rs.{(basePrice - opt.price).toFixed(2)}
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className={`text-base font-black font-mono ${isDiscount ? 'text-emerald-600' : 'text-slate-900'}`}>
                    Rs.{opt.price.toFixed(2)}
                  </p>
                  {isDiscount && (
                    <p className="text-[10px] text-slate-400 line-through font-mono">Rs.{basePrice.toFixed(2)}</p>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-slate-500 hover:text-slate-800 font-bold px-3 py-1.5 rounded-lg hover:bg-slate-200/60 transition-all cursor-pointer"
          >
            Cancel (Esc)
          </button>
          <button
            type="button"
            onClick={() => {
              const opt = options[selectedIdx];
              if (opt) onSelect(opt.price, opt.label);
            }}
            className="text-xs font-black bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-xl shadow-md transition-all cursor-pointer flex items-center gap-1.5"
          >
            <span>Select Price</span>
            <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded font-mono">↵ Enter</span>
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

